import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DocumentReaderComponent } from '../document-reader/document-reader.component';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { DocumentReaderService } from '../services/document-reader.service';
import { SessionStorageService } from '../services/session-storage.service';
import { AnnuityDialogComponent } from '../annuity-lookup/annuity-dialog/annuity-dialog.component';
import { BeaconServiceV2 } from '../services/beaconv2.service';
import { FormlyModule } from '@ngx-formly/core';
import { RixtremaDialogComponent } from '../rixtrema/rixtrema-dialog/rixtrema-dialog.component';
import { RixtremaService } from '../rixtrema/rixtrema.service';
import { UnifiedFlowService } from '../unified-flow/unified.service';

export type WizardStepType = 'dtcc' | 'document' | 'beacon' | 'search401k';

export interface WizardStep {
  id: WizardStepType;
  label: string;
  question: string;
  choice: boolean | null;
  complete: boolean;
}

@Component({
  selector: 'app-lookup-file-wizard',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    DocumentReaderComponent,
    MatStepperModule,
    AnnuityDialogComponent,
    FormlyModule,
    RixtremaDialogComponent,
  ],
  templateUrl: './lookup-file-wizard.component.html',
  styleUrl: './lookup-file-wizard.component.scss',
})
export class LookupFileWizardComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;

  private dialogRef =
    inject<MatDialogRef<LookupFileWizardComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  private changeDetector = inject(ChangeDetectorRef);
  private beaconSrcV2 = inject(BeaconServiceV2);
  private docReadSvc = inject(DocumentReaderService);
  private ss = inject(SessionStorageService);
  private rixtremaSrc = inject(RixtremaService);
  private unifiedFlowService = inject(UnifiedFlowService);

  steps: WizardStep[] = [];

  get hasAnyChoice(): boolean {
    return this.steps.some(s => s.choice === true);
  }

  // Toggle these before init to control which steps are shown
  showDtcc = true;
  showDocument = true;
  showBeacon = true;
  showF5500 = true;

  dataDTCC: {
    companies: null;
    index: unknown;
    group: unknown;
    profile: unknown;
    varset: unknown;
    showFixedRate: boolean;
    autoSelect: 'yes' | 'no';
  } = {
    companies: null,
    index: undefined,
    group: undefined,
    profile: undefined,
    varset: undefined,
    showFixedRate: false,
    autoSelect: 'yes',
  };
  dataF5500 = false;
  dtccDataOut: any;
  documentDataOut: any;
  beaconDataOut: any;
  F5500DataOut: any;
  finalDataset: any = {};

  ngOnInit(): void {
    this.buildSteps();
  }

  buildSteps(): void {
    this.showDocument = this.data.opts.find(opt => opt.value === '3');
    this.showDtcc = this.data.opts.find(
      opt =>
        opt.value === '2' &&
        this.data.buttonType === 'beacon' &&
        this.ss.get('rights').includes('DTCCDataAccess')
    );
    this.showBeacon = this.data.opts.find(
      opt =>
        opt.value === '2' &&
        this.data.buttonType === 'beacon' &&
        this.ss.get('rights').includes('CreateSamlResponse')
    );
    this.showF5500 = this.data.opts.find(
      opt => opt.value === '2' && this.data.buttonType === 'search401k'
    );
    if (this.showF5500) {
      this.data['dataF5500'] = true;
    }
    const definitions: Array<{
      enabled: boolean;
      id: WizardStepType;
      label: string;
      question: string;
      defaultYes: boolean;
    }> = [
      {
        enabled: this.showDtcc,
        id: 'dtcc',
        label: 'Policy Lookup',
        question: 'Are you the agent of record?',
        defaultYes: false,
      },
      {
        enabled: this.showDocument,
        id: 'document',
        label: 'Document Upload',
        question: 'Do you have a document to upload?',
        defaultYes: true,
      },
      {
        enabled: this.showBeacon,
        id: 'beacon',
        label: 'Annuity Lookup',
        question: 'Would you like to look up a historical annuity?',
        defaultYes: true,
      },
      {
        enabled: this.showF5500,
        id: 'search401k',
        label: 'Search for Employer Plan',
        question:
          this.data.opts?.find(opt => opt.value === '2')?.label ||
          'Search For Plan',
        defaultYes: true,
      },
    ];
    this.steps = definitions
      .filter(d => d.enabled)
      .map(({ enabled: _enabled, ...d }) => ({
        ...d,
        choice: d.defaultYes ? true : null,
        complete: false,
      }));

    this.steps.forEach(step => {
      if (step.choice) {
        this.answer(step, true);
      }
    });
  }

  triggerDTCC(autoSelect: 'yes' | 'no'): void {
    const field = this.data.fieldData;
    const model = field?.parent?.parent?.parent?.model;
    Object.assign(this.data, {
      companies: null,
      index: this.data.index,
      group: this.data.group,
      profile: this.data.profile,
      varset: this.data.varset,
      showFixedRate:
        model?.UnitExchange_ShowCurrentFixedRate?.toLowerCase() === 'y' &&
        model?.UnitExchange_ShowMinimumFixedRate?.toLowerCase() === 'y',
      autoSelect,
    });
  }

  answer(step: WizardStep, choice: boolean): void {
    step.choice = choice;
    if (!choice) {
      switch (step.id) {
        case 'dtcc':
          this.dtccDataOut = null;
          Object.assign(this.finalDataset, { dtcc: this.dtccDataOut });
          break;
        case 'document':
          this.documentDataOut = null;
          Object.assign(this.finalDataset, {
            document: this.documentDataOut,
          });
          break;
        case 'beacon':
          this.beaconDataOut = null;
          Object.assign(this.finalDataset, { beacon: this.beaconDataOut });
          break;
        case 'search401k':
          this.F5500DataOut = null;
          Object.assign(this.finalDataset, { search401k: this.F5500DataOut });
          break;

        default:
          break;
      }
      step.complete = true;
      this.changeDetector.detectChanges();
      this.stepper.next();
    } else if (step.id === 'dtcc') {
      this.triggerDTCC('yes');
    } else if (step.id === 'beacon') {
      this.triggerDTCC('no');
    }
  }

  completeStep(step: WizardStep): void {
    step.complete = true;
    this.changeDetector.detectChanges();
    this.stepper.next();
  }

  onStepChange(ev: StepperSelectionEvent): void {
    const step = this.steps[ev.selectedIndex];
    if (step && !step.choice) {
      step.choice = null;
      step.complete = false;
    }
  }

  finish(): void {
    // The following is for future functionality. Currenlty doesn't do anything so it's commented
    // this.checkForDuplicates();
    // loop through keys and perform the ops for each item from doc reader component and annuity dialog component
    let documentReadingJSON;
    const finalData = { data: {} };
    // The first source with a defined value claims each routing field.
    const claimRouting = (source: Record<string, unknown>) => {
      ['group', 'index', 'varset'].forEach(field => {
        const value = source[field];
        if (
          !Object.keys(finalData).includes(field) &&
          value !== undefined &&
          !(typeof value === 'number' && Number.isNaN(value))
        ) {
          Object.assign(finalData, { [field]: source[field] });
        }
      });
    };
    if (Object.keys(this.finalDataset).length) {
      Object.keys(this.finalDataset).forEach(key => {
        if (this.finalDataset[key]) {
          switch (key) {
            case 'dtcc':
              Object.keys(this.finalDataset[key].data).forEach(keyVal => {
                if (!Object.keys(finalData.data).includes(keyVal))
                  Object.assign(finalData.data, {
                    [keyVal]: this.finalDataset[key].data[keyVal],
                  });
              });
              claimRouting(this.finalDataset[key]);
              break;
            case 'document':
              documentReadingJSON = this.finalDataset[key].docData;
              Object.keys(this.finalDataset[key].docData).forEach(keyVal => {
                if (!Object.keys(finalData.data).includes(keyVal))
                  Object.assign(finalData.data, {
                    [keyVal]: this.finalDataset[key].docData[keyVal],
                  });
              });
              claimRouting(this.finalDataset[key].docData);
              break;
            case 'beacon':
              Object.keys(this.finalDataset[key].data).forEach(keyVal => {
                if (!Object.keys(finalData.data).includes(keyVal))
                  Object.assign(finalData.data, {
                    [keyVal]: this.finalDataset[key].data[keyVal],
                  });
              });
              claimRouting(this.finalDataset[key]);
              break;
            case 'search401k':
              Object.keys(this.finalDataset[key]).forEach(keyVal => {
                if (!Object.keys(finalData.data).includes(keyVal))
                  Object.assign(finalData.data, {
                    [keyVal]: this.finalDataset[key][keyVal],
                  });
              });
              claimRouting(this.finalDataset[key]);
              Object.assign(finalData.data, {
                inputWizard: true,
              });
              break;
            default:
              break;
          }
        }
      });
    }
    if (this.finalDataset['dtcc'] || this.finalDataset['beacon']) {
      this.beaconSrcV2.annuityData(finalData);
    }
    if (this.finalDataset['search401k']) {
      this.rixtremaSrc.planData(finalData.data);
    }
    this.unifiedFlowService.setLoading(false);
    if (documentReadingJSON) {
      this.dialogRef.close({
        DocumentReadingJSON: documentReadingJSON,
      });
    } else {
      this.dialogRef.close();
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  handleInputMethodOut(ev, step) {
    switch (step.id) {
      case 'dtcc':
        this.dtccDataOut = ev;
        Object.assign(this.finalDataset, { dtcc: this.dtccDataOut });
        break;
      case 'document':
        if (ev?.docData) this.documentDataOut = ev;
        if (ev?.docDataDetail) {
          this.documentDataOut.docData['fundDetails'] = ev.docDataDetail;
          this.documentDataOut.docData['fundDetailIDs'] = ev.fundDetailIDs;
        }
        Object.assign(this.finalDataset, { document: this.documentDataOut });
        break;
      case 'beacon':
        this.beaconDataOut = ev;
        Object.assign(this.finalDataset, { beacon: this.beaconDataOut });
        break;
      case 'search401k':
        this.F5500DataOut = ev;
        Object.assign(this.finalDataset, { search401k: this.F5500DataOut });
        break;

      default:
        break;
    }
    step.choice = true;
    this.completeStep(step);
  }

  checkForDuplicates() {
    const keyMap: Record<string, string[]> = {
      SponsorSubsidy: ['Expenses'],
      CompanyMatch: ['Match', 'SafeHarborMatch'],
      InserviceDistributions: ['InService'],
      InsuranceServices: ['InsuranceServices'],
      SelfDirectedV2: ['SelfDirectedFeatureV2'],
      BrokerageWindow: ['BrokerageWindowFeature'],
      LIFeature: ['LifeInsuranceFeature'],
      ManagedOption: ['ManagedOption'],
      AccessToAdvisorLevel: ['AccessToAdvisorLevel'],
      PlanName: ['PLAN_NAME'],
    };
    const inputsUsed = Object.keys(this.finalDataset);
    if (inputsUsed.includes('search401k') && inputsUsed.includes('document')) {
      const docKeys = Object.keys(this.finalDataset['document'].docData);
      const planKeys = Object.keys(this.finalDataset['search401k']);
      const duplicates = docKeys.filter(
        key =>
          planKeys.includes(key) ||
          keyMap[key]?.some(mapped => planKeys.includes(mapped))
      );
      // This will be expanded in the future to have the user select the item they want to use
      // between duplicates, and the points from doc reader and f5500 have different naming.
      // If both doc read and f5500 are used, this fn finds any f5500 fields that are duplicated
      // from doc read. ('duplicates' is a list of fields from doc read that were found in f5500).

      // console.log('Duplicate keys:', duplicates);
    }
  }
}
